import { createFileRoute } from '@tanstack/react-router';
import { Group, NumberInput, SegmentedControl, Stack, Text, Textarea } from '@mantine/core';
import { ClipboardCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, EmptyState, PageContainer, PageHeader, Pill, Section, Table, type TableColumn } from '../components/ui';
import { ErrorState } from '../components/common/ErrorState';
import { LoadingState } from '../components/common/LoadingState';
import { useReviewCompletion, useReviewQueue } from '../hooks/useGradebook';
import type { ReviewQueueItem } from '../api/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = (createFileRoute as any)('/admin/review')({
  component: ReviewQueue,
});

/**
 * Review queue — answers the auto-grader could not decide (a rule task
 * submitted while the VM was offline, a rubric with no facts) plus anything an
 * instructor wants to override. The decision replaces the automatic grade.
 */
function ReviewQueue() {
  const q = useReviewQueue();
  const review = useReviewCompletion();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selected = useMemo(() => q.data?.find((i) => i.completion_id === selectedId) ?? null, [q.data, selectedId]);

  const columns: ReadonlyArray<TableColumn<ReviewQueueItem>> = useMemo(
    () => [
      { key: 'student', header: 'Student', cell: (i) => <Text size="sm" fw={500}>{i.student.username}</Text> },
      { key: 'lab', header: 'Lab', cell: (i) => <Text size="sm">{i.lab_name}</Text> },
      {
        key: 'task',
        header: 'Task',
        width: '38%',
        cell: (i) => (
          <Group gap={6} wrap="nowrap" align="center">
            <Text size="sm" style={{ minWidth: 0 }}>{i.task.title}</Text>
            <Badge variant="info" size="xs" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{i.check_type}</Badge>
          </Group>
        ),
      },
      { key: 'pts', header: 'Points', align: 'right', cell: (i) => <Text size="sm" ff="JetBrains Mono">{i.task.points}</Text> },
      {
        key: 'when',
        header: 'Submitted',
        cell: (i) => <Text size="xs" c="dimmed" ff="JetBrains Mono">{i.submitted_at ? new Date(i.submitted_at).toLocaleString() : '—'}</Text>,
      },
    ],
    [],
  );

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={ClipboardCheck}
        title="Review queue"
        description="Answers waiting on you. Your decision replaces the automatic grade."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Admin', to: '/admin' }, { label: 'Review' }]}
        actions={q.data ? <Pill variant={q.data.length ? 'warn' : 'success'} size="sm">{q.data.length} pending</Pill> : undefined}
      />
      <div style={{ display: 'grid', gridTemplateColumns: selected ? 'minmax(0, 1fr) minmax(320px, 440px)' : '1fr', gap: 16, alignItems: 'start' }}>
        <Section title="Pending">
          {q.isError ? (
            <ErrorState title="Could not load the queue" message={(q.error as Error)?.message ?? ''} onRetry={() => void q.refetch()} />
          ) : q.isPending ? (
            <LoadingState rows={5} height={30} />
          ) : (q.data?.length ?? 0) === 0 ? (
            <EmptyState title="Nothing to review" description="Every submission in your cohorts has a grade." />
          ) : (
            <Table
              rows={q.data ?? []}
              rowKey={(i) => String(i.completion_id)}
              columns={columns}
              onRowClick={(i) => setSelectedId(i.completion_id)}
              rowLabel={(i) => `Review ${i.student.username} — ${i.task.title}`}
            />
          )}
        </Section>
        {selected ? (
          <ReviewPanel
            key={selected.completion_id}
            item={selected}
            saving={review.isPending}
            onCancel={() => setSelectedId(null)}
            onSubmit={(d) =>
              review.mutate({ completionId: selected.completion_id, ...d }, { onSuccess: () => setSelectedId(null) })
            }
          />
        ) : null}
      </div>
    </PageContainer>
  );
}

function ReviewPanel({
  item, saving, onCancel, onSubmit,
}: {
  item: ReviewQueueItem;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (d: { is_correct: boolean; points_earned: number; notes: string }) => void;
}) {
  const [verdict, setVerdict] = useState<'correct' | 'incorrect'>('correct');
  const [points, setPoints] = useState<number>(item.task.points);
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (verdict === 'incorrect') setPoints(0);
    else if (points === 0) setPoints(item.task.points);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verdict]);

  return (
    <Card>
      <Stack gap="md">
        <div>
          <Text fw={600} size="sm">{item.task.title}</Text>
          <Text size="xs" c="dimmed">{item.student.username} · {item.lab_name} · attempt {item.attempts_used}</Text>
        </div>
        {item.task.description ? <Text size="xs" c="dimmed">{item.task.description}</Text> : null}
        <div>
          <Text size="xs" fw={600} c="dimmed" mb={4}>Student's answer</Text>
          <Text size="sm" ff="JetBrains Mono" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 260, overflow: 'auto' }}>
            {item.answer ?? '—'}
          </Text>
        </div>
        {item.solution_excerpt ? (
          <div>
            <Text size="xs" fw={600} c="dimmed" mb={4}>What the grader looks for</Text>
            <Text size="xs">{item.solution_excerpt}</Text>
          </div>
        ) : null}
        <SegmentedControl
          value={verdict}
          onChange={(v) => setVerdict(v as 'correct' | 'incorrect')}
          data={[{ label: 'Correct', value: 'correct' }, { label: 'Incorrect', value: 'incorrect' }]}
          aria-label="Verdict"
        />
        <NumberInput
          label={`Points (max ${item.task.points})`}
          value={points}
          onChange={(v) => setPoints(typeof v === 'number' ? v : Number(v) || 0)}
          min={0}
          max={item.task.points}
          disabled={verdict === 'incorrect'}
        />
        <Textarea label="Note to the student" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} autosize minRows={2} maxLength={4000} />
        <Group justify="flex-end" gap="xs">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button
            variant="primary"
            loading={saving}
            onClick={() => onSubmit({ is_correct: verdict === 'correct', points_earned: verdict === 'correct' ? points : 0, notes })}
          >
            Save review
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
